import { Component } from '@theme/component';

/**
 * TRB scroll-reveal — a quiet fade + rise as an element scrolls into view.
 *
 * Used for the two "premium = restraint" reveals in the design system:
 *   - Shop: the lone product card fades + rises on load (docs §6.4 / §4).
 *   - About: the editorial image + manifesto reveal in a gentle stagger
 *     (docs §6.6 / §4).
 *
 * Design rules honoured (docs/trb-design-system.md §4 Motion):
 *   - Single quiet fade + ~12px rise. Distance comes from `--trb-reveal-distance`,
 *     easing from `--trb-ease-out-quad` — tokens only, never literals.
 *   - Init on visibility via IntersectionObserver (CLAUDE.md architecture rule),
 *     so nothing animates until it nears the viewport and the work is deferred.
 *   - Fully `prefers-reduced-motion` aware: under reduce, the component does
 *     nothing — content is shown immediately with no transform/opacity change.
 *   - Pure progressive enhancement: the hidden/pre-reveal state is applied by
 *     THIS script (it adds `.trb-reveal--armed`), so with JS off or before hydrate
 *     the content is fully visible. We never ship a "hidden until JS" flash.
 *
 * Stagger: child targets reveal in document order, each delayed by
 * `--trb-reveal-stagger` (default 90ms) * its index, for the gentle About cascade.
 *
 * Markup:
 *   <trb-reveal>                 reveals itself as one unit, OR
 *   <trb-reveal data-stagger>    reveals its direct [data-reveal] children in sequence, OR
 *   <trb-reveal data-reveal-ancestor=".product-card">
 *                                reveals the closest matching ancestor instead of
 *                                itself — used on the Shop card, whose wrapper is
 *                                emitted by Horizon's grid and can't be wrapped in
 *                                JSON, so a marker <trb-reveal> sits inside it.
 *
 * @extends Component
 */
class TrbReveal extends Component {
  // Pure progressive enhancement: no refs are required, the element is fully
  // usable (visible) without any of them resolving.
  requiredRefs = [];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {MediaQueryList | null} */
  #reducedMotion = null;

  connectedCallback() {
    super.connectedCallback();

    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    // Respect reduced motion: reveal everything immediately, attach nothing.
    if (this.#reducedMotion.matches) {
      this.#revealAll();
      return;
    }

    // Arm the pre-reveal (hidden) state from JS so there is no flash-of-hidden
    // for no-JS / pre-hydrate users.
    const targets = this.#targets();
    for (const [index, target] of targets.entries()) {
      target.classList.add('trb-reveal--armed');
      target.style.setProperty('--trb-reveal-index', String(index));
    }

    // If IntersectionObserver is unavailable, just reveal.
    if (!('IntersectionObserver' in window)) {
      this.#revealAll();
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries, observer) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.#revealAll();
          observer.disconnect();
          break;
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );

    // Observe the first target's box (the card / image), not the marker, so the
    // reveal fires when the visible element nears the viewport.
    this.#observer.observe(targets[0] ?? this);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /**
   * The elements that animate. With `data-reveal-ancestor`, the closest matching
   * ancestor reveals (the host stays a zero-impact marker). With `data-stagger`,
   * the direct [data-reveal] children cascade. Otherwise the host reveals as one
   * unit.
   * @returns {HTMLElement[]}
   */
  #targets() {
    const ancestorSelector = this.getAttribute('data-reveal-ancestor');
    if (ancestorSelector) {
      const ancestor = this.closest(ancestorSelector);
      if (ancestor instanceof HTMLElement) return [ancestor];
    }
    if (this.hasAttribute('data-stagger')) {
      const children = Array.from(this.querySelectorAll(':scope > [data-reveal]'));
      if (children.length) return /** @type {HTMLElement[]} */ (children);
    }
    return [this];
  }

  /** Reveals every target and removes the armed (hidden) state. */
  #revealAll() {
    for (const target of this.#targets()) {
      target.classList.remove('trb-reveal--armed');
      target.classList.add('trb-reveal--in');
    }
  }
}

if (!customElements.get('trb-reveal')) {
  customElements.define('trb-reveal', TrbReveal);
}
